<template>
  <!-- A confirmation toast ("Color Copied!"): white, so it stands out against
       the dark page it floats over. Place it in a ToastStack. Hides itself
       after `autoHideDuration`. -->
  <transition
    enter-active-class="transform ease-out duration-300 transition"
    enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
    enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="showNotification" class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5">
      <div class="flex items-center gap-4 py-2.5 pl-4 pr-2.5">
        <p class="min-w-0 flex-1 text-sm font-medium text-gray-900">{{ message }}</p>
        <CloseButton tone="light" @click="closeNotification" />
      </div>
    </div>
  </transition>
</template>

<script setup>
import { onBeforeUnmount, watch } from 'vue'
import CloseButton from '~/components/atoms/CloseButton.vue'

const props = defineProps({
  showNotification: Boolean,
  message: {
    type: String,
    default: 'Notification Text',
  },
  autoHideDuration: {
    type: Number,
    default: 3000, // 3 seconds auto-hide by default
  },
})

const emit = defineEmits(['update:showNotification'])

function closeNotification() {
  emit('update:showNotification', false)
}

// Automatically hide the notification after a delay.
//
// The pending timer is cleared first, so a notification raised again while
// one is already on screen (a second copy, a different message) gets the full
// delay instead of being cut short by the previous one.
let hideTimer = null

watch([() => props.showNotification, () => props.message], ([visible]) => {
  if (hideTimer) clearTimeout(hideTimer)
  if (!visible) return
  hideTimer = setTimeout(() => {
    hideTimer = null
    closeNotification()
  }, props.autoHideDuration)
})

onBeforeUnmount(() => {
  if (hideTimer) clearTimeout(hideTimer)
})
</script>
